import * as THREE from 'three';

import { service_open_pose } from '@/services/open_pose/service_open_pose';
import { ConstantsOpenPose } from '../constants_open_pose';

const {
  BILLBOARD_DEFAULT_COLOR,
  BLACK_BACKGROUND_MULTIPLIER,
  BILLBOARD_NAME,
  BLACK_BACKGROUND_NAME,
  BILLBOARD_POSITION_Y,
  BLACK_BACKGROUND_POSITION_Y,
  BLACK_BACKGROUND_MATERIAL_COLOR,
} = ConstantsOpenPose;

export class OpenPoseBillboardController {
  /*
   * FIELDS
   */

  private billboard: THREE.Mesh;
  private billboard_texture: THREE.Texture;

  private black_background: THREE.Mesh;

  private is_perspective_camera: boolean;

  width: number;
  height: number;

  /*
   * METHODS
   */

  public get_width(): number {
    return this.width;
  }

  public get_height(): number {
    return this.height;
  }

  constructor() {
    this.width = ConstantsOpenPose.BILLBOARD_DIMENSIONS_WIDTH;
    this.height = ConstantsOpenPose.BILLBOARD_DIMENSIONS_HEIGHT;

    this.is_perspective_camera = true;

    this.billboard = new THREE.Mesh();
    this.billboard_texture = new THREE.Texture();

    this.black_background = new THREE.Mesh();

    this.billboard.name = BILLBOARD_NAME;
    this.black_background.name = BLACK_BACKGROUND_NAME;
  }

  public create_billboard() {
    service_open_pose.scene.remove(this.billboard);

    this.dispose_mesh(this.billboard);
    this.dispose_texture(this.billboard_texture);

    const plane_geometry = new THREE.PlaneGeometry(this.width, this.height);
    const material = new THREE.MeshBasicMaterial({ color: BILLBOARD_DEFAULT_COLOR, transparent: true, side: THREE.DoubleSide });

    const mesh = new THREE.Mesh(plane_geometry, material);
    mesh.position.set(0, this.height / 2, BILLBOARD_POSITION_Y);
    mesh.name = BILLBOARD_NAME;

    this.billboard = mesh;
    service_open_pose.scene.add(mesh);

    this.create_black_background();
    this.update_black_background_visibility();
  }

  public update_billboard(texture: THREE.Texture, width: number, height: number) {
    service_open_pose.scene.remove(this.billboard);
    this.dispose_mesh(this.billboard);
    this.dispose_texture(this.billboard_texture);

    this.width = width;
    this.height = height;

    this.billboard_texture = texture;

    const plane_geometry = new THREE.PlaneGeometry(this.width, this.height);
    const material = new THREE.MeshBasicMaterial({ map: texture, transparent: true, side: THREE.DoubleSide });

    const mesh = new THREE.Mesh(plane_geometry, material);
    mesh.position.set(0, this.height / 2, BILLBOARD_POSITION_Y);
    mesh.name = BILLBOARD_NAME;

    this.billboard = mesh;
    service_open_pose.scene.add(mesh);

    this.create_black_background();
    this.update_black_background_visibility();
  }

  public create_black_background() {
    service_open_pose.scene.remove(this.black_background);
    this.dispose_mesh(this.black_background);

    const background_width = this.width * BLACK_BACKGROUND_MULTIPLIER;
    const background_height = this.height * BLACK_BACKGROUND_MULTIPLIER;

    const plane_geometry = new THREE.PlaneGeometry(background_width, background_height);
    const basic_material = new THREE.MeshBasicMaterial({ color: BLACK_BACKGROUND_MATERIAL_COLOR, side: THREE.DoubleSide });

    const background = new THREE.Mesh(plane_geometry, basic_material);
    background.position.set(0, this.height / 2, BLACK_BACKGROUND_POSITION_Y);
    background.name = BLACK_BACKGROUND_NAME;

    this.black_background = background;

    if (!this.is_perspective_camera) service_open_pose.scene.add(background);
  }

  public update_black_background_visibility() {
    const is_camera_perspective = this.is_perspective_camera;
    const exists = service_open_pose.scene.children.includes(this.black_background);

    if (is_camera_perspective && exists) return service_open_pose.scene.remove(this.black_background);

    if (!is_camera_perspective && !exists) return service_open_pose.scene.add(this.black_background);
  }

  public set_is_perspective_camera(value: boolean) {
    this.is_perspective_camera = value;
  }

  /*
   * DISPOSE
   */

  public dispose() {
    service_open_pose.scene.remove(this.billboard);
    service_open_pose.scene.remove(this.black_background);

    this.dispose_mesh(this.billboard);
    this.dispose_mesh(this.black_background);
    this.dispose_texture(this.billboard_texture);
  }

  private dispose_mesh(mesh: THREE.Mesh) {
    if (mesh.geometry) mesh.geometry.dispose();

    const materials = mesh.material;
    if (Array.isArray(materials)) {
      for (const material of materials) material.dispose();
    } else if (materials) {
      (materials as THREE.Material).dispose();
    }
  }

  private dispose_texture(texture: THREE.Texture) {
    texture.dispose();
  }
}
